import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import storageManager from '../utils/StorageManager';

const SettingsContext = createContext();

const defaultSettings = {
  rubric: {
    criteria: [
      { id: '1', name: 'Accuracy', weight: 40, description: 'Correctness of the answer' },
      { id: '2', name: 'Completeness', weight: 30, description: 'Thoroughness of the response' },
      { id: '3', name: 'Clarity', weight: 20, description: 'Clear and understandable explanation' },
      { id: '4', name: 'Originality', weight: 10, description: 'Unique insights or perspectives' }
    ],
    totalWeight: 100
  },
  gradingCurve: {
    enabled: false,
    curveType: 'linear',
    curveStrength: 50,
    targetAverage: 75,
    minScore: 40
  },
  similaritySettings: {
    contextualWeight: 60,
    semanticWeight: 40,
    threshold: 60,
    strictMode: false,
    allowPartialCredit: true,
    partialCreditThreshold: 40,
    enableContext: true
  },
  generalSettings: {
    autoSave: true,
    defaultLLM: 'deepseek',
    showDetailedFeedback: true,
    theme: 'dark' // Default to dark mode
  },
  apiSettings: {
    timeout: 30,
    enableCache: true,
    maxRetries: 3,
    retryDelay: 1000
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from IndexedDB on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const savedSettings = await storageManager.loadSettings();
        if (savedSettings) {
          // Merge saved settings with defaults
          setSettings(prev => ({
            ...prev,
            ...savedSettings,
            rubric: { ...prev.rubric, ...savedSettings.rubric },
            gradingCurve: { ...prev.gradingCurve, ...savedSettings.gradingCurve },
            similaritySettings: { ...prev.similaritySettings, ...savedSettings.similaritySettings },
            generalSettings: { 
              ...prev.generalSettings, 
              ...savedSettings.generalSettings,
              theme: savedSettings.generalSettings?.theme || 'dark' // Default to dark mode
            },
            apiSettings: { ...prev.apiSettings, ...savedSettings.apiSettings }
          }));
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Auto-save settings when they change
  useEffect(() => {
    if (!isLoading && settings.generalSettings.autoSave) {
      const saveTimeout = setTimeout(() => {
        storageManager.saveSettings(settings).catch(error => {
          console.error('Error auto-saving settings:', error);
        });
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(saveTimeout);
    }
  }, [settings, isLoading]);

  // Apply theme to document when settings change
  useEffect(() => {
    if (!isLoading) {
      const theme = settings.generalSettings.theme || 'light';
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  }, [settings.generalSettings.theme, isLoading]);

  const addRubricCriterion = useCallback((criterion) => {
    setSettings(prev => {
      const newCriterion = {
        ...criterion,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
      };
      
      const newCriteria = [...prev.rubric.criteria, newCriterion];
      const totalWeight = newCriteria.reduce((sum, c) => sum + c.weight, 0);
      
      return {
        ...prev,
        rubric: {
          criteria: newCriteria,
          totalWeight
        }
      };
    });
  }, []);

  const removeRubricCriterion = useCallback((criterionId) => {
    setSettings(prev => {
      const newCriteria = prev.rubric.criteria.filter(c => c.id !== criterionId);
      const totalWeight = newCriteria.reduce((sum, c) => sum + c.weight, 0);
      
      return {
        ...prev,
        rubric: {
          criteria: newCriteria,
          totalWeight
        }
      };
    });
  }, []);

  const updateGradingCurve = useCallback((updates) => {
    setSettings(prev => ({
      ...prev,
      gradingCurve: { ...prev.gradingCurve, ...updates }
    }));
  }, []);

  const updateSimilaritySettings = useCallback((updates) => {
    setSettings(prev => ({
      ...prev,
      similaritySettings: { ...prev.similaritySettings, ...updates }
    }));
  }, []);

  const updateGeneralSettings = useCallback((updates) => {
    setSettings(prev => ({
      ...prev,
      generalSettings: { ...prev.generalSettings, ...updates }
    }));
  }, []);

  const updateApiSettings = useCallback((updates) => {
    setSettings(prev => ({
      ...prev,
      apiSettings: { ...prev.apiSettings, ...updates }
    }));
  }, []);

  const toggleTheme = useCallback(() => {
    setSettings(prev => ({
      ...prev,
      generalSettings: {
        ...prev.generalSettings,
        theme: prev.generalSettings.theme === 'dark' ? 'light' : 'dark'
      }
    }));
  }, []);

  const calculateCurvedScore = useCallback((rawScore) => {
    const { enabled, curveType, curveStrength, targetAverage, minScore } = settings.gradingCurve;
    
    if (!enabled) return rawScore;
    
    let curvedScore = rawScore;
    
    switch (curveType) {
      case 'linear':
        // Linear adjustment toward target average
        const adjustment = (targetAverage - rawScore) * (curveStrength / 100);
        curvedScore = rawScore + adjustment;
        break;
        
      case 'exponential':
        // Exponential curve - boosts lower scores more
        const boost = Math.pow((100 - rawScore) / 100, 2) * curveStrength;
        curvedScore = rawScore + boost;
        break;
        
      case 'logarithmic':
        // Logarithmic curve - smooth adjustment
        const logAdjustment = Math.log10(rawScore + 1) * curveStrength;
        curvedScore = rawScore + logAdjustment;
        break;
    }
    
    // Ensure score stays within bounds
    curvedScore = Math.max(minScore, Math.min(100, curvedScore));
    
    return Math.round(curvedScore * 10) / 10; // Round to 1 decimal place
  }, [settings.gradingCurve]);

  const resetSettings = useCallback(() => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings(defaultSettings);
      storageManager.saveSettings(defaultSettings).catch(error => {
        console.error('Error saving reset settings:', error);
      });
    }
  }, []);

  const saveSettingsToStorage = useCallback(async () => {
    try {
      await storageManager.saveSettings(settings);
      return true;
    } catch (error) {
      console.error('Error saving settings:', error);
      return false;
    }
  }, [settings]);

  const value = {
    settings,
    isLoading,
    addRubricCriterion,
    removeRubricCriterion,
    updateGradingCurve,
    updateSimilaritySettings,
    updateGeneralSettings,
    updateApiSettings,
    toggleTheme, // Added toggleTheme to context value
    calculateCurvedScore,
    resetSettings,
    saveSettingsToStorage
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
